import React from 'react';

import { Group, Paper, Stack, Text, useMantineTheme } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import {
  ResponsiveContainer,
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { UpcomingRevenueProjection } from 'types';

interface ProjectedRevenueByMonthChartsPanelProps {
  revenueProjectionData: UpcomingRevenueProjection;
}

const ProjectedRevenueByMonthChartsPanel = ({
  revenueProjectionData,
}: ProjectedRevenueByMonthChartsPanelProps): JSX.Element => {
  const theme = useMantineTheme();
  const { formatCurrency } = useLocalization();

  const formatChartData = (): Array<{
    month: string;
    total: number;
  }> => {
    return revenueProjectionData.monthlyProjections.map(projection => {
      const monthAbbreviation = dayjs(projection.month).format('MMM');
      return {
        month: monthAbbreviation,
        total: Number(projection.total),
      };
    });
  };

  const customTooltip = ({ active, payload, label }: any): JSX.Element | null => {
    if (active && payload && payload.length) {
      return (
        <Paper p='sm' shadow='md'>
          <Text weight={600}>{label}</Text>
          {payload.map((entry: any, index: number) => (
            <Text key={index} style={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </Text>
          ))}
        </Paper>
      );
    }
    return null;
  };

  const chartData = formatChartData();

  // Calculate dynamic Y-axis ticks with 3k steps
  const calculateYAxisTicks = (): number[] => {
    const maxValue = Math.max(...revenueProjectionData.monthlyProjections.map(p => Number(p.total)));
    const stepSize = 3000;
    const maxTick = Math.ceil(maxValue / stepSize) * stepSize;

    const ticks = [];
    for (let i = 0; i <= maxTick; i += stepSize) {
      ticks.push(i);
    }

    return ticks;
  };

  const yAxisTicks = calculateYAxisTicks();

  return (
    <>
      <Group position='apart' noWrap spacing='lg' my='xl'>
        <Group>
          <IconAlertTriangle size={32} strokeWidth={1.5} color={theme.colors.kiloBlue[9]} />
        </Group>
        <Stack spacing='xs'>
          <Text size='lg' color='kiloBlue.9' fw={600}>
            Monthly Revenue Breakdown
          </Text>
          <Text color='kiloBlue.8'>
            The chart below shows how your total projected revenue is distributed across each month in the
            selected date range
          </Text>
        </Stack>
      </Group>

      <Paper withBorder py='md' px='lg'>
        <Stack>
          <Text fw={600} size='lg' color='dark.4'>
            Projected Revenue by Month
          </Text>

          <ResponsiveContainer width='100%' height={160}>
            <RechartsBarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray='3 3' />
              <XAxis dataKey='month' />
              <YAxis
                tickFormatter={value => `${Math.round(value / 1000)}k`}
                domain={[0, 'dataMax']}
                ticks={yAxisTicks}
              />
              <Tooltip content={customTooltip} />
              <Bar dataKey='total' fill={theme.colors.green[6]} radius={[8, 8, 0, 0]} />
            </RechartsBarChart>
          </ResponsiveContainer>

          <Text align='center' color='dimmed'>
            Each bar represents the projected revenue for that month, based on auto-renewing packages.
          </Text>
        </Stack>
      </Paper>
    </>
  );
};
export default ProjectedRevenueByMonthChartsPanel;
